import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiClient from "utils/apiClient";

/* ── Icons ── */
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
  </svg>
);
const InboxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
    <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

/* ── Constants ── */
const ACTION_BADGE = { create: "success", update: "warning", delete: "danger" };
const SOURCE_BADGE = { lms: "brand", tenant_management: "info" };
const SOURCE_LABELS = { lms: "LMS EDUNARA", tenant_management: "Tenant" };
const METHOD_BADGE = { GET: "neutral", POST: "success", PUT: "warning", PATCH: "warning", DELETE: "danger" };

/* ── Helpers ── */
const MONTHS_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

function Avatar({ name }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const COLORS = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6"];
  const bg = COLORS[(name || "?").charCodeAt(0) % COLORS.length];
  return <div className="lms-avatar lms-avatar-sm" style={{ background: bg }}>{initials}</div>;
}

function ActivityLogPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [detailLog, setDetailLog] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.get("/tenants", { params: { per_page: 100 } }).then((res) => {
      if (res.data?.status === "success") setTenants(res.data.data || []);
    }).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (tenantFilter) params.tenant_id = tenantFilter;
      const res = await apiClient.get("/activity-logs", { params });
      if (res.data?.status === "success") {
        setLogs(res.data.data || []);
        setMeta(res.data.meta || null);
      }
    } catch {}
    setLoading(false);
  }, [page, search, actionFilter, sourceFilter, tenantFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { document.title = "Activity Log | LMS Tenant"; }, []);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const resetFilters = () => {
    setSearch(""); setSearchInput(""); setActionFilter("");
    setSourceFilter(""); setTenantFilter(""); setPage(1);
  };

  const tenantNameMap = {};
  tenants.forEach((t) => { tenantNameMap[t.id] = t.name; });

  const hasActiveFilter = search || actionFilter || sourceFilter || tenantFilter;

  const handleCopyJson = (data) => {
    const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const renderJsonBlock = (data, label) => {
    if (!data) return null;
    const formatted = typeof data === "string" ? data : JSON.stringify(data, null, 2);
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span className="t-eyebrow">{label}</span>
          <button
            onClick={() => handleCopyJson(data)}
            className="lms-btn lms-btn-ghost lms-btn-sm"
            style={{ gap: 4, fontSize: 12, height: 28, padding: "0 8px" }}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? t("Tersalin!") : t("Salin")}
          </button>
        </div>
        <pre style={{ background: "var(--slate-900)", color: "#e2e8f0", padding: "14px 16px", borderRadius: 10, fontSize: 12, lineHeight: 1.6, overflow: "auto", maxHeight: 320, margin: 0, fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
          {formatted}
        </pre>
      </div>
    );
  };

  const startRow = meta ? (meta.current_page - 1) * 15 + 1 : 1;
  const endRow = meta ? Math.min(meta.current_page * 15, meta.total) : logs.length;

  return (
    <div className="lms-page lms-anim-fade">
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span style={{ cursor: "pointer" }} onClick={() => history.push("/admin/dashboard")}>Dashboard</span>
            <ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Activity Log")}</span>
          </div>
          <div className="t-display" style={{ marginTop: 4 }}>{t("Activity Log")}</div>
          <div className="t-small lms-text-muted" style={{ marginTop: 2 }}>{t("Riwayat aktivitas seluruh sistem")}</div>
        </div>
        <button className="lms-btn lms-btn-secondary" onClick={fetchLogs} disabled={loading}>
          <RefreshIcon /> {t("Refresh")}
        </button>
      </div>

      <div className="lms-card">
        {/* Filter bar */}
        <div className="lms-card-header" style={{ flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "flex-start" }}>
          <div className="lms-input-wrap" style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
            <SearchIcon />
            <input
              className="lms-input"
              placeholder={t("Cari aktivitas...")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <select
            className="lms-select"
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            style={{ width: 140 }}
          >
            <option value="">{t("Semua Aksi")}</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
          <select
            className="lms-select"
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); if (e.target.value !== "lms") setTenantFilter(""); setPage(1); }}
            style={{ width: 160 }}
          >
            <option value="">{t("Semua Sumber")}</option>
            <option value="lms">LMS EDUNARA</option>
            <option value="tenant_management">Tenant</option>
          </select>
          {sourceFilter === "lms" && (
            <select
              className="lms-select"
              value={tenantFilter}
              onChange={(e) => { setTenantFilter(e.target.value); setPage(1); }}
              style={{ width: 200 }}
            >
              <option value="">{t("Semua Tenant")}</option>
              {tenants.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
          )}
          {hasActiveFilter && (
            <button className="lms-btn lms-btn-secondary" onClick={resetFilters}>{t("Reset")}</button>
          )}
        </div>

        {/* Table */}
        <div className="lms-table-wrap">
          <table className="lms-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>{t("Waktu")}</th>
                <th>{t("User")}</th>
                <th>{t("Aksi")}</th>
                <th>{t("Module")}</th>
                <th>{t("Deskripsi")}</th>
                <th>{t("Sumber")}</th>
                <th>{t("Tenant")}</th>
                <th style={{ width: 56 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9}>
                    <div className="lms-empty" style={{ padding: "48px 24px" }}>
                      <div className="lms-spinner" style={{ width: 28, height: 28 }} />
                      <span className="t-small lms-text-muted">{t("Memuat data...")}</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="lms-empty">
                      <div className="lms-empty-icon"><InboxIcon /></div>
                      <div className="t-h3">{t("Tidak ada data")}</div>
                      <p className="t-small lms-text-muted" style={{ margin: 0 }}>
                        {hasActiveFilter ? t("Tidak ada log yang sesuai dengan filter saat ini") : t("Belum ada activity log yang tercatat")}
                      </p>
                      {hasActiveFilter && (
                        <button className="lms-btn lms-btn-secondary lms-btn-sm" onClick={resetFilters} style={{ marginTop: 4 }}>
                          {t("Reset Filter")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={log.id}>
                    <td className="t-caption lms-text-muted">{(page - 1) * 15 + i + 1}</td>
                    <td>
                      <span className="t-caption lms-text-muted" style={{ whiteSpace: "nowrap" }}>
                        {fmtDateTime(log.created_at)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar name={log.user_name} />
                        <span className="td-strong" style={{ fontSize: 13 }}>{log.user_name || "—"}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`lms-badge lms-badge-no-dot lms-badge-${ACTION_BADGE[log.action] || "neutral"}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="t-small lms-text-muted">{log.module || "—"}</td>
                    <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {log.description || "—"}
                    </td>
                    <td>
                      <span className={`lms-badge lms-badge-no-dot lms-badge-${SOURCE_BADGE[log.source] || "neutral"}`}>
                        {SOURCE_LABELS[log.source] || log.source}
                      </span>
                    </td>
                    <td className="t-caption lms-text-muted">
                      {log.tenant_id ? (tenantNameMap[log.tenant_id] || `ID ${log.tenant_id}`) : "—"}
                    </td>
                    <td>
                      <button
                        className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon"
                        onClick={() => setDetailLog(log)}
                        title={t("Lihat Detail")}
                      >
                        <EyeIcon />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.last_page > 1 && (
          <div className="lms-pagination">
            <span className="lms-pagination-info">
              {t("Menampilkan")} <strong>{startRow}–{endRow}</strong> {t("dari")} <strong>{meta.total}</strong> {t("log")}
            </span>
            <div className="lms-pagination-controls">
              <button
                className="lms-btn lms-btn-secondary lms-btn-sm"
                disabled={meta.current_page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t("← Prev")}
              </button>
              <span className="t-small lms-text-muted" style={{ padding: "0 4px", minWidth: 60, textAlign: "center" }}>
                {meta.current_page} / {meta.last_page}
              </span>
              <button
                className="lms-btn lms-btn-secondary lms-btn-sm"
                disabled={meta.current_page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("Next →")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailLog && (
        <div className="lms-modal-overlay" onClick={() => setDetailLog(null)}>
          <div className="lms-modal lms-modal-lg lms-anim-slide" onClick={(e) => e.stopPropagation()}>
            <div className="lms-modal-header">
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-strong)" }}>
                  {t("Detail Log")} <span className="t-mono" style={{ fontSize: 14, color: "var(--text-muted)" }}>#{detailLog.id}</span>
                </div>
                <div className="t-caption lms-text-muted" style={{ marginTop: 2 }}>
                  {fmtDateTime(detailLog.created_at)}
                </div>
              </div>
              <button className="lms-modal-close" onClick={() => setDetailLog(null)}>
                <XIcon />
              </button>
            </div>

            <div className="lms-modal-body">
              <div className="lms-grid-2" style={{ rowGap: 16 }}>
                  <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("User")}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar name={detailLog.user_name} />
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: "var(--text-strong)" }}>
                      {detailLog.user_name || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Aksi")}</div>
                  <span className={`lms-badge lms-badge-no-dot lms-badge-${ACTION_BADGE[detailLog.action] || "neutral"}`}>
                    {detailLog.action}
                  </span>
                </div>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Module")}</div>
                  <div style={{ fontSize: 13.5, color: "var(--text-base)" }}>{detailLog.module || "—"}</div>
                </div>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Sumber")}</div>
                  <span className={`lms-badge lms-badge-no-dot lms-badge-${SOURCE_BADGE[detailLog.source] || "neutral"}`}>
                    {SOURCE_LABELS[detailLog.source] || detailLog.source}
                  </span>
                </div>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Method")}</div>
                  {detailLog.method ? (
                    <span className={`lms-badge lms-badge-no-dot lms-badge-${METHOD_BADGE[detailLog.method] || "neutral"}`}
                      style={{ fontFamily: "monospace" }}>
                      {detailLog.method}
                    </span>
                  ) : <span className="t-small lms-text-muted">—</span>}
                </div>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("IP Address")}</div>
                  <div className="t-mono" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                    {detailLog.ip_address || "—"}
                  </div>
                </div>
                {detailLog.tenant_id && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Tenant")}</div>
                    <div style={{ fontSize: 13.5, color: "var(--text-base)" }}>
                      {tenantNameMap[detailLog.tenant_id] || `ID ${detailLog.tenant_id}`}
                    </div>
                  </div>
                )}
                <div style={{ gridColumn: "1 / -1" }}>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("Deskripsi")}</div>
                  <div style={{ fontSize: 13.5, color: "var(--text-base)", lineHeight: 1.6 }}>
                    {detailLog.description || "—"}
                  </div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("URL")}</div>
                  <div className="t-mono" style={{ fontSize: 12.5, color: "var(--text-muted)", background: "var(--slate-50)", padding: "8px 12px", borderRadius: 8, wordBreak: "break-all", border: "1px solid var(--border-subtle)", lineHeight: 1.6 }}>
                    {detailLog.url || "—"}
                  </div>
                </div>
                {detailLog.user_agent && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className="t-eyebrow" style={{ marginBottom: 6 }}>{t("User Agent")}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, wordBreak: "break-word" }}>
                      {detailLog.user_agent}
                    </div>
                  </div>
                )}
              </div>

              {detailLog.request_data && renderJsonBlock(detailLog.request_data, t("Request Data"))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLogPage;
